import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { FormSuccess } from 'Components/Form/Success';
import { Main as Container } from 'Components/Main';
import { addUser, editUser, getUserById } from 'services/users-service';
import { getUsersGroups } from 'services/users-groups-service';
import { useQuery } from 'react-query';
import { getCompanyRolesBySlug } from 'services/company-service';
import { Dialog, Autocomplete, PageHeader, Select, TextField } from 'Components/ui';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CancelBtn = styled.button`
  height: 44px;
  padding: 0 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-celeste);
  background: white;
  border: 1.5px solid var(--vo-celeste);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover { background: var(--vo-mist); }
`;

const SaveBtn = styled.button`
  height: 44px;
  padding: 0 24px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: var(--vo-celeste);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover { background: var(--vo-cobalto); }
`;

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const CardIconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const RequiredStar = styled.span`
  color: var(--vo-ink-2);
`;


const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-danger);
  margin: 2px 0 0;
`;

const HelperMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 2px 0 0;
`;

// Phone row
const PhoneRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

// companyRoles are keyed by roleKey, not _id/id — Select's default
// isOptionEqualToValue (reference-first, then _id/id fallback) would never
// engage its fallback for these objects, so a cloned role value (RHF's
// Controller clones on every render) would silently fail to match its
// counterpart in a freshly-fetched companyRoles list. The exact
// GroupsTagInput/Bonds/Managers bug, one level removed — explicit
// comparator required, Select's default isn't enough here.
const isSameRole = (option, value) => option === value || option?.roleKey === value?.roleKey

// Some group names have no server-side trim/validation (raw Mongo driver,
// no schema) — a stray leading/trailing/doubled space would desync the
// <input>'s displayed text from getOptionLabel(value) on blur and silently
// clear the selection, the same MUI Autocomplete gotcha hit with framework
// names in AssessmentWizard. Collapsing whitespace keeps the label stable.
const groupLabel = (g) => (g.name || '').replace(/\s+/g, ' ').trim();

// ─── Component ────────────────────────────────────────────────────────────────

export const UsersForm = () => {
  const { t } = useTranslation(['user', 'common']);
  const { id } = useParams();
  const selectedTenant = useSelector(useAuthTenant);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const effectivePhoneCountry = phoneCountry ?? selectedTenant?.country;
  const { allConfigs } = useCountryConfig(effectivePhoneCountry);
  const navigate = useNavigate();
  const [idUser, setIdUser] = useState(null);
  const [finished, setFinished] = useState(false);

  const { data: userGroups } = useQuery(['getUserGroups'], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getUsersGroups(selectedTenant.slug);
    return result?.data?.length > 0 ? result.data : [];
  });

  const { data: companyRoles } = useQuery(['getCompanyRoles', selectedTenant.slug], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getCompanyRolesBySlug(selectedTenant.slug);
    return result?.data?.length > 0 ? result.data : [];
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      active: true,
      name: '',
      email: '',
      cellphone: '',
      groups: [],
      permissions: [],
    },
  });

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdUser(null);
    navigate('/users/add');
  };

  const { data: user, isLoading } = useQuery(['getUser', id], async () => {
    if (!id) return null;
    const result = await getUserById(id);
    return result?.data ?? null;
  });

  const formatUser = values => {
    const permissionsArray = Array.isArray(values.permissions)
      ? values.permissions
      : values.permissions ? [values.permissions] : [];

    if (id) {
      const groupsUser = user?.groups?.filter(g => g.companyKey !== selectedTenant.slug) ?? [];
      return {
        name: values.name,
        cellphone: values.cellphone,
        email: values.email,
        groups: [
          ...groupsUser,
          ...values.groups.map(g => ({ companyKey: selectedTenant.slug, name: g.name })),
        ],
        permissions: [
          ...user.permissions.filter(p => p.companyKey !== selectedTenant.slug),
          ...permissionsArray.map(p => ({ companyKey: selectedTenant.slug, roleKey: p.roleKey })),
        ],
        active: user.active,
      };
    }
    return {
      name: values.name,
      cellphone: values.cellphone,
      email: values.email,
      active: true,
      groups: values.groups.map(g => ({ companyKey: selectedTenant.slug, name: g.name })),
      permissions: permissionsArray.map(p => ({ companyKey: selectedTenant.slug, roleKey: p.roleKey })),
    };
  };

  const onSubmit = async () => {
    try {
      const res = id
        ? await editUser(id, selectedTenant.slug, formatUser(getValues()))
        : await addUser(formatUser(getValues()));
      if (res.status === 200 || res.status === 201) {
        setIdUser(res.data._id ?? id);
        setFinished(true);
      } else {
        throw new Error('Ocorreu um erro ao tentar salvar o usuário.');
      }
    } catch (error) {
      Dialog.fire({
        title: t('user:alert.errorTitle'),
        html: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  useEffect(() => {
    if (!id || !user || !userGroups) return;
    Object.keys(user).forEach(key => {
      if (key === 'permissions') {
        const userPermissions = user[key].filter(item => item.companyKey === selectedTenant.slug);
        setValue(key, userPermissions);
      } else if (key === 'groups') {
        const filtered = user[key]?.filter(g => g.companyKey === selectedTenant.slug) ?? [];
        const full = filtered
          .map(ug => userGroups?.find(g => g.name === ug.name) || null)
          .filter(g => g !== null && g._id);
        setValue(key, full);
      } else {
        setValue(key, user[key]);
      }
    });
  }, [id, user, selectedTenant, setValue, userGroups]);

  if (id && isLoading) return null;

  return (
    <Container>
      <PageWrap>
        {/* Header */}
        <PageHeader
          title={id ? t('user:users.form.editTitle') : t('user:users.form.addTitle')}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('user:users.title'), route: '/users' },
            { label: id ? t('user:users.form.editTitle') : t('user:users.form.addTitle') },
          ]}
          actions={
            !finished && (
              <HeaderActions data-tour="form-actions">
                <CancelBtn type="button" onClick={() => navigate('/users')}>
                  {t('user:users.form.cancel')}
                </CancelBtn>
                <SaveBtn type="submit" form="user-form">
                  {t('user:users.form.save')}
                </SaveBtn>
              </HeaderActions>
            )
          }
        />

        {!finished ? (
          <form id="user-form" onSubmit={handleSubmit(onSubmit)}>
            <FormWrap>
              {/* Card 1 — Dados do usuário */}
              <FormCard>
                <CardHeader>
                  <CardIconBox $gradient="linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </CardIconBox>
                  <CardTitleWrap>
                    <CardTitle>{t('user:users.form.card1Title')}</CardTitle>
                    <CardSubtitle>{t('user:users.form.card1Subtitle')}</CardSubtitle>
                  </CardTitleWrap>
                </CardHeader>

                <FieldGrid>
                  {/* Name */}
                  <FieldGroup data-tour="user-name">
                    <FieldLabel htmlFor="user-name-input">
                      {t('user:users.form.name')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    <TextField
                      id="user-name-input"
                      fullWidth
                      error={!!errors?.name}
                      autoFocus
                      placeholder={t('user:users.form.namePlaceholder')}
                      {...register('name', { required: t('user:users.form.validation.nameRequired') })}
                    />
                    {errors?.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
                  </FieldGroup>

                  {/* Email */}
                  <FieldGroup data-tour="user-email">
                    <FieldLabel htmlFor="user-email-input">
                      {t('user:users.form.email')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    <TextField
                      id="user-email-input"
                      type="email"
                      fullWidth
                      error={!!errors?.email}
                      placeholder={t('user:users.form.emailPlaceholder')}
                      {...register('email', { required: t('user:users.form.validation.emailRequired') })}
                    />
                    {errors?.email && <ErrorMsg>{errors.email.message}</ErrorMsg>}
                  </FieldGroup>

                  {/* Phone — left column only */}
                  <FieldGroup data-tour="user-phone">
                    <FieldLabel>{t('user:users.form.phone')}</FieldLabel>
                    <PhoneRow>
                      {allConfigs.length > 0 && (
                        <Select
                          name="phoneCountry"
                          value={effectivePhoneCountry || ''}
                          onChange={value => setPhoneCountry(value)}
                          options={allConfigs.map(c => ({ value: c.countryCode, label: c.phonePrefix }))}
                          fullWidth={false}
                          sx={{ width: 90, flexShrink: 0 }}
                          aria-label={t('user:users.form.phone')}
                        />
                      )}
                      <TextField
                        sx={{ flex: 1 }}
                        placeholder={t('user:users.form.phonePlaceholder')}
                        {...register('cellphone')}
                      />
                    </PhoneRow>
                  </FieldGroup>
                </FieldGrid>
              </FormCard>

              {/* Card 2 — Acesso e permissões */}
              <FormCard>
                <CardHeader>
                  <CardIconBox $gradient="linear-gradient(135deg,var(--vo-warning),var(--vo-warning))">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </CardIconBox>
                  <CardTitleWrap>
                    <CardTitle>{t('user:users.form.card2Title')}</CardTitle>
                    <CardSubtitle>{t('user:users.form.card2Subtitle')}</CardSubtitle>
                  </CardTitleWrap>
                </CardHeader>

                <FieldGrid>
                  {/* Groups */}
                  <FieldGroup data-tour="user-groups">
                    <FieldLabel>{t('user:users.form.group')}</FieldLabel>
                    <Controller
                      control={control}
                      name="groups"
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          multiple
                          filterSelectedOptions
                          isOptionEqualToValue={(option, val) => option._id === val._id}
                          options={userGroups || []}
                          getOptionLabel={groupLabel}
                          value={value || []}
                          onChange={onChange}
                          placeholder={t('user:users.form.groupSearch')}
                          helperText={t('user:users.form.groupHelper')}
                        />
                      )}
                    />
                  </FieldGroup>

                  {/* Permission */}
                  <FieldGroup data-tour="user-permissions">
                    <FieldLabel htmlFor="user-permission-select">
                      {t('user:users.form.permission')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    <Controller
                      control={control}
                      name="permissions"
                      rules={{ required: t('user:users.form.validation.permissionRequired') }}
                      render={({ field: { onChange, value } }) => {
                        const selectedRole = Array.isArray(value) && value.length > 0 ? value[0] : (value || '');
                        return (
                          <>
                            <Select
                              id="user-permission-select"
                              name="permissions"
                              value={selectedRole}
                              onChange={onChange}
                              error={!!errors?.permissions}
                              isOptionEqualToValue={isSameRole}
                              options={[
                                { value: '', label: t('user:users.form.selectPermission'), disabled: true },
                                ...(companyRoles || []).map(item => ({ value: item, label: item.roleName })),
                              ]}
                            />
                            {errors?.permissions && <ErrorMsg>{errors.permissions.message}</ErrorMsg>}
                            <HelperMsg>{t('user:users.form.permissionHelper')}</HelperMsg>
                          </>
                        );
                      }}
                    />
                  </FieldGroup>
                </FieldGrid>
              </FormCard>
            </FormWrap>
          </form>
        ) : (
          <FormCard style={{ maxWidth: '720px' }}>
            <FormSuccess
              title={t('user:users.form.successTitle')}
              labelStartAgain={t('user:users.form.addAnother')}
              handleStartAgain={handleReset}
              labelShow={t('user:users.form.viewDetails')}
              handleShow={() => navigate('/users/show/' + idUser)}
            />
          </FormCard>
        )}
      </PageWrap>
    </Container>
  );
};
